import { FormInput } from "@general-backoffice/core/index";
import type { User } from "@investhinks/api-client";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import useUsers from "./hooks/useUsers";

const normalize = (str: string) =>
  str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

interface UserOption {
  value: number | string;
  label: string;
  data: User;
}

interface UserSelectProps {
  user: User | User[] | null;
  setUser: (user: User | User[] | null) => void;
  isMulti?: boolean;
  isDisabled?: boolean;
  required?: boolean;
  [key: string]: unknown;
}

const UserSelect = ({ user, setUser, isMulti, ...props }: UserSelectProps) => {
  const { t } = useTranslation("users", { keyPrefix: "userSelect" });
  const { users, isLoading } = useUsers({
    filters: { role: { type: { $eq: "authenticated" } } },
  });

  const options = useMemo(
    () =>
      (users || []).map((user) => ({
        value: user.id,
        label: user.email,
        data: user,
      })),
    [users]
  );

  const filterOptions = ({ label }: { label: string }, input: string) => normalize(label).includes(normalize(input));
  return (
    <FormInput.Select
      placeholder={t("searchPlaceholder")}
      // Render the menu in a portal so it escapes the modal body. Callers used
      // to reserve room for it with a fixed 350px spacer, which pushed the
      // modal's own buttons below the fold on small screens.
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
      menuPlacement="auto"
      {...props}
      onChange={
        isMulti
          ? (options: UserOption[]) => setUser(options.map((h: UserOption) => h?.data))
          : (option: UserOption | null) => setUser(option?.data || null)
      }
      value={
        isMulti
          ? user &&
            Array.isArray(user) &&
            user.map((u) => options.find(({ value }: UserOption) => `${value}` === `${u.id}`))
          : user && !Array.isArray(user) && options.find(({ value }: UserOption) => `${value}` === `${user.id}`)
      }
      options={options}
      isDisabled={isLoading || props.isDisabled}
      filterOption={filterOptions}
      isClearable
      isMulti={isMulti}
    />
  );
};

export default UserSelect;
